'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';

import type { VariantProps } from 'class-variance-authority';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Viewport>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[26.25rem]',
			className,
		)}
		{...props}
	/>
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
	'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
	{
		variants: {
			variant: {
				default: 'border bg-background text-foreground',
				info: 'border-brand bg-brand-subtle text-brand',
				success: 'border-success bg-success-subtle text-success',
				error: 'border-destructive bg-destructive-subtle text-destructive',
				warning: 'border-warning bg-warning-subtle text-warning',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
);

const Toast = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
	const variantColor =
		variant === 'success'
			? 'bg-success'
			: variant === 'error'
				? 'bg-destructive'
				: variant === 'warning'
					? 'bg-warning'
					: 'bg-brand';
	return (
		<ToastPrimitives.Root ref={ref} className={cn(toastVariants({ variant }), className, variant)} {...props}>
			<div className="flex items-start">
				{variant === 'warning' && <TriangleAlert className="text-warning mr-2 w-6 h-6 flex-shrink-0" />}
				{variant === 'error' && <CircleAlert className="text-destructive mr-2 w-6 h-6 flex-shrink-0" />}
				{variant === 'success' && <CircleCheck className="text-success mr-2   w-6 h-6 flex-shrink-0" />}
				{variant === 'info' && <Info className="text-brand mr-2 w-6 h-6 flex-shrink-0" />}
				<div className="flex flex-col space-y-1">{children}</div>
				<div className="absolute bottom-0 left-0 h-1 w-full">
					<div className={`h-full ${variantColor}  animate-reduce-width`}></div>
				</div>
			</div>
			{/* <ToastClose /> */}
		</ToastPrimitives.Root>
	);
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastClose = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Close>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			'absolute right-2 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 group-hover:opacity-100 group-[.error]:text-destructive group-[.error]:hover:text-destructive group-[.error]:focus:ring-red-400 group-[.error]:focus:ring-offset-red-600 group-[.info]:text-brand group-[.info]:hover:text-brand group-[.info]:focus:ring-brand group-[.info]:focus:ring-offset-brand group-[.warning]:text-warning group-[.warning]:hover:text-warning group-[.warning]:focus:ring-amber-400 group-[.warning]:focus:ring-offset-amber-600 group-[.success]:text-success group-[.success]:hover:text-success group-[.success]:focus:ring-emerald-400 group-[.success]:focus:ring-offset-emerald-600',
			className,
		)}
		toast-close=""
		aria-label="Close"
		{...props}>
		<X className="h-4 w-4" aria-hidden="true" />
	</ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Title>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Title ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
	React.ElementRef<typeof ToastPrimitives.Description>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Description ref={ref} className={cn('text-sm opacity-90', className)} {...props} />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

export {
	type ToastProps,
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
};
